import { Command } from 'cmdk';
import { useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FiUser, FiCode, FiCpu, FiBriefcase, FiAward, FiMail,
  FiTerminal, FiMusic, FiGrid, FiSearch, FiPower,
} from 'react-icons/fi';
import { useFocusTrap } from '../../hooks/useKeyboardShortcuts';
import { useWindowsStore } from '../../store/windowsStore';

const APPS = [
  { id: 'about',        label: 'About me',         section: 'Apps',     icon: FiUser,      shortcut: null },
  { id: 'projects',     label: 'Projects',         section: 'Apps',     icon: FiCode,      shortcut: null },
  { id: 'skills',       label: 'Skills',           section: 'Apps',     icon: FiCpu,       shortcut: null },
  { id: 'experience',   label: 'Experience',       section: 'Apps',     icon: FiBriefcase, shortcut: null },
  { id: 'certificates', label: 'Certificates',     section: 'Apps',     icon: FiAward,     shortcut: null },
  { id: 'contact',      label: 'Contact',          section: 'Apps',     icon: FiMail,      shortcut: null },
  { id: 'terminal',     label: 'Terminal',         section: 'Apps',     icon: FiTerminal,  shortcut: 'Cmd+`' },
  { id: 'lofi',         label: 'lofi.fm',          section: 'Apps',     icon: FiMusic,     shortcut: 'Cmd+L' },
];

const ACTIONS = [
  { id: 'toggle-wallpaper', label: 'Toggle wallpaper (matrix ↔ grid)', section: 'Actions', icon: FiGrid,    shortcut: 'Cmd+M' },
  { id: 'toggle-lofi',      label: 'Toggle lofi player',               section: 'Actions', icon: FiMusic,   shortcut: 'Cmd+L' },
  { id: 'toggle-terminal',  label: 'Toggle terminal',                 section: 'Actions', icon: FiTerminal,shortcut: 'Cmd+`' },
  { id: 'reset-layout',     label: 'Reset window layout',             section: 'Actions', icon: FiPower,   shortcut: null },
];

/**
 * CommandPalette — Cmd/Ctrl+K. Fuzzy search across apps + actions.
 * Estilo cyberpunk glassmorphism, neon-cyan focus.
 */
export default function CommandPalette({ open, onClose, onAction }) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  useFocusTrap({ containerRef, active: open, onOutsideClick: onClose });

  const openWindow = useWindowsStore((s) => s.openWindow);
  const closeWindow = useWindowsStore((s) => s.closeWindow);
  const resetLayout = useWindowsStore((s) => s.resetLayout);

  const runApp = useCallback((id) => {
    openWindow(id);
    onClose();
  }, [openWindow, onClose]);

  const runAction = useCallback((id) => {
    switch (id) {
      case 'toggle-wallpaper': onAction('wallpaper'); break;
      case 'toggle-lofi':      onAction('lofi'); break;
      case 'toggle-terminal':  onAction('terminal'); break;
      case 'reset-layout':     resetLayout(); break;
      default: break;
    }
    onClose();
  }, [onAction, onClose, resetLayout]);

  // Reset input ao abrir
  useEffect(() => {
    if (open) {
      // Pequeno delay p/ cmdk montar
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const sections = useMemo(() => {
    const apps = APPS.map((a) => ({ ...a, kind: 'app' }));
    const actions = ACTIONS.map((a) => ({ ...a, kind: 'action' }));
    return { apps, actions };
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] px-4 bg-black/60 backdrop-blur-sm"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={containerRef}
        className="w-full max-w-[560px] rounded-xl border border-[var(--neon-cyan)]/40 bg-[var(--bg-deep)]/95 backdrop-blur-xl shadow-[0_0_60px_rgba(0,240,255,0.3)] font-mono text-sm overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <Command
          className="flex flex-col"
          label="Command palette"
          loop
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--neon-cyan)]/15">
            <FiSearch size={14} className="text-[var(--neon-cyan)] shrink-0" />
            <Command.Input
              ref={inputRef}
              placeholder="Search apps, actions…"
              className="flex-1 bg-transparent outline-none text-[var(--text-primary)] placeholder:text-[var(--text-muted)] caret-[var(--neon-cyan)]"
              autoFocus
            />
            <kbd className="hidden sm:inline-block text-[0.65rem] uppercase tracking-wider text-[var(--text-muted)] border border-[var(--neon-cyan)]/20 rounded px-1.5 py-0.5">
              esc
            </kbd>
          </div>

          <Command.List className="max-h-[55vh] overflow-y-auto py-2">
            <Command.Empty className="px-4 py-6 text-center text-[var(--text-muted)] text-xs">
              no results found
            </Command.Empty>

            <Command.Group heading="Apps" className="px-1 pb-2">
              {sections.apps.map((app) => (
                <Command.Item
                  key={app.id}
                  value={`app ${app.label} ${app.id}`}
                  onSelect={() => runApp(app.id)}
                  className="flex items-center gap-3 px-3 py-2 mx-1 rounded cursor-pointer aria-selected:bg-[var(--neon-cyan)]/15 aria-selected:text-[var(--neon-cyan)] text-[var(--text-secondary)] transition-colors"
                >
                  <app.icon size={14} className="shrink-0" />
                  <span className="flex-1">{app.label}</span>
                  {app.shortcut && (
                    <kbd className="text-[0.6rem] uppercase text-[var(--text-muted)] border border-[var(--neon-cyan)]/15 rounded px-1.5 py-0.5">
                      {app.shortcut}
                    </kbd>
                  )}
                </Command.Item>
              ))}
            </Command.Group>

            <Command.Group heading="Actions" className="px-1 pb-2">
              {sections.actions.map((action) => (
                <Command.Item
                  key={action.id}
                  value={`action ${action.label} ${action.id}`}
                  onSelect={() => runAction(action.id)}
                  className="flex items-center gap-3 px-3 py-2 mx-1 rounded cursor-pointer aria-selected:bg-[var(--neon-violet)]/15 aria-selected:text-[var(--neon-violet)] text-[var(--text-secondary)] transition-colors"
                >
                  <action.icon size={14} className="shrink-0" />
                  <span className="flex-1">{action.label}</span>
                  {action.shortcut && (
                    <kbd className="text-[0.6rem] uppercase text-[var(--text-muted)] border border-[var(--neon-violet)]/15 rounded px-1.5 py-0.5">
                      {action.shortcut}
                    </kbd>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>

          <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[var(--neon-cyan)]/15 text-[0.65rem] text-[var(--text-muted)] uppercase tracking-wider">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="border border-[var(--neon-cyan)]/20 rounded px-1 py-0.5">↑</kbd>
                <kbd className="border border-[var(--neon-cyan)]/20 rounded px-1 py-0.5">↓</kbd>
                <span>navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="border border-[var(--neon-cyan)]/20 rounded px-1 py-0.5">↵</kbd>
                <span>select</span>
              </span>
            </div>
            <span className="text-[var(--neon-cyan)]">Cmd+K</span>
          </div>
        </Command>
      </div>
    </div>
  );
}
